import React from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import dayjs from 'dayjs';
import { useAppContext } from 'providers/AppProvider';
import { useTranslation } from 'react-i18next';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { LineChart } from 'echarts/charts';
import { CanvasRenderer } from 'echarts/renderers';
import { ThemeVariant } from 'config';
import { tooltipFormatterList } from 'helpers/echart-utils';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  LineChart,
  CanvasRenderer,
  LegendComponent
]);

interface DailyCount {
  date: string;
  count: number;
  amount: number;
}

interface EcomTotalSellsChartProps {
  currentMonthData: DailyCount[];
  prevMonthData: DailyCount[];
  periodLabel: string;
  isPeriodMonth: boolean;
  period?: number;
  showAmount?: boolean;
  showBoth?: boolean;
}

const EcomTotalSellsChart: React.FC<EcomTotalSellsChartProps> = ({
  currentMonthData = [],
  prevMonthData = [],
  periodLabel = '',
  isPeriodMonth = true,
  period = 1,
  showAmount = false,
  showBoth = false
}) => {
  const { t } = useTranslation();
  const {
    config: { theme },
    getThemeColor
  } = useAppContext();

  // Prepare data for the chart
  const formatChartData = () => {
    // Extract dates and counts or amounts based on showAmount prop
    const dates = currentMonthData.map(item => new Date(item.date));

    // If showBoth is true, we'll show both count and amount in different series
    if (showBoth) {
      const currentCounts = currentMonthData.map(item => item.count);
      const currentAmounts = currentMonthData.map(item => item.amount);
      const prevCounts = prevMonthData.map(item => item.count);
      const prevAmounts = prevMonthData.map(item => item.amount);

      return {
        dates,
        currentCounts,
        currentAmounts,
        prevCounts,
        prevAmounts
      };
    } else {
      // Original behavior - single value type (count or amount)
      const currentValues = currentMonthData.map(item => showAmount ? item.amount : item.count);
      const prevValues = prevMonthData.map(item => showAmount ? item.amount : item.count);

      return {
        dates,
        currentValues,
        prevValues
      };
    }
  };

  const chartData = formatChartData();
  const dates = chartData.dates;

  // Calculate interval based on data length for appropriate axis labels
  const calculateInterval = () => {
    if (dates.length <= 10) return 1;
    if (dates.length <= 31) return Math.floor(dates.length / 5);
    return Math.floor(dates.length / 7);
  };

  const interval = calculateInterval();

  // Generate series names
  const generateSeriesNames = () => {
    if (showBoth) {
      const currentPeriodLabel = isPeriodMonth
        ? t('salesSummary.last30days')
        : period === 2
          ? t('salesSummary.last60days')
          : t('salesSummary.last90days');

      return {
        currentCountName: t('chart.currentOrders'),
        currentAmountName: t('chart.currentAmount'),
        prevCountName: t('chart.previousOrders'),
        prevAmountName: t('chart.previousAmount')
      };
    } else {
      const valueType = showAmount ? t('transactionSummary.revenue') : t('orders');

      return {
        currentName: t('chart.current') + ' ' +
          (isPeriodMonth
            ? t('salesSummary.last30days')
            : period === 2
              ? t('salesSummary.last60days')
              : t('salesSummary.last90days')) + ' ' + valueType,

        prevName: t('chart.previous') + ' ' +
          (isPeriodMonth
            ? t('salesSummary.last30days')
            : period === 2
              ? t('salesSummary.last60days')
              : t('salesSummary.last90days')) + ' ' + valueType
      };
    }
  };

  const seriesNames = generateSeriesNames();

  // Build a mapping of current period indexes to previous period indexes
  const buildIndexMapping = () => {
    const mapping: Record<number, number> = {};
    
    // Map current period day numbers to their indexes
    const currentDayToIndexMap: Record<number, number> = {};
    currentMonthData.forEach((item, index) => {
      const dayNumber = dayjs(item.date).date();
      currentDayToIndexMap[dayNumber] = index;
    });
    
    // Find corresponding indexes in previous period
    prevMonthData.forEach((item, prevIndex) => {
      const dayNumber = dayjs(item.date).date();
      if (currentDayToIndexMap[dayNumber] !== undefined) {
        mapping[currentDayToIndexMap[dayNumber]] = prevIndex;
      }
    });
    
    return mapping;
  };
  
  const currentToPrevIndexMap = buildIndexMapping();

  const getChartOptions = () => ({
    color: showBoth
      ? [getThemeColor('primary'), getThemeColor('success'), getThemeColor('info'), getThemeColor('warning')]
      : [showAmount ? getThemeColor('success') : getThemeColor('primary'), getThemeColor('info')],
    tooltip: {
      trigger: 'axis',
      padding: 10,
      backgroundColor: getThemeColor('body-highlight-bg'),
      borderColor: getThemeColor('border-color'),
      textStyle: { color: getThemeColor('light-text-emphasis') },
      borderWidth: 1,
      transitionDuration: 0,
      axisPointer: {
        type: 'none'
      },
      formatter: (params: any) => {
        let tooltipText = '';
        const isCurrent: Record<string, boolean> = {};
        const isAmount: Record<string, boolean> = {};
        const values: Record<string, number> = {};
        const colors: Record<string, string> = {};

        // The hovered index in the current period data
        const hoveredIndex = params[0].dataIndex;
        
        // Get corresponding index in previous period data
        const correspondingPrevIndex = currentToPrevIndexMap[hoveredIndex];
        
        // Get the dates from the specific data points
        const currentDateFromData = hoveredIndex < currentMonthData.length
          ? new Date(currentMonthData[hoveredIndex].date)
          : null;

        const prevDateFromData = correspondingPrevIndex !== undefined && correspondingPrevIndex < prevMonthData.length
          ? new Date(prevMonthData[correspondingPrevIndex].date)
          : null;

        // Group by series type and save values
        params.forEach((param: any) => {
          const seriesName = param.seriesName;
          // Use a language-independent way to determine if a series is current or previous
          const current = seriesName === seriesNames.currentCountName ||
                          seriesName === seriesNames.currentAmountName ||
                          seriesName === seriesNames.currentName;
          const amount = seriesName === seriesNames.currentAmountName ||
                         seriesName === seriesNames.prevAmountName ||
                         (showAmount && (seriesName === seriesNames.currentName || seriesName === seriesNames.prevName));
          const value = param.value;

          // Create a key based on whether it's current/previous and count/amount
          const key = (current ? 'current_' : 'prev_') + (amount ? 'amount' : 'count');

          isCurrent[key] = current;
          isAmount[key] = amount;
          values[key] = value;
          colors[key] = param.color;
        });

        // Format the dates using the actual date objects
        const currentDate = currentDateFromData
          ? dayjs(currentDateFromData).format('D MMM')
          : '';

        const prevDate = prevDateFromData
          ? dayjs(prevDateFromData).format('D MMM')
          : '';

        // Add each relevant value to the tooltip
        Object.keys(values).forEach(key => {
          // Skip if we don't have the corresponding date
          if ((key.startsWith('current_') && !currentDate) || 
              (key.startsWith('prev_') && !prevDate)) {
            return;
          }
          
          const value = values[key];
          const current = isCurrent[key];
          const amount = isAmount[key];
          const date = current ? currentDate : prevDate;

          // Get appropriate color for this data type
          const color = colors[key] || (amount ? getThemeColor('success') : getThemeColor('primary'));

          tooltipText += `<div style="margin: 3px 0">
            <span style="background-color: ${color}; width: 8px; height: 8px; display: inline-block; margin-right: 5px; border-radius: 50%;"></span>
            <span>${date}: <strong>${value}${amount ? ' ' + t('transactionSummary.dinarSymbol') : ''}</strong></span>
          </div>`;
        });

        return tooltipText;
      }
    },
    legend: {
      show: true,
      top: 0,
      left: 'center',
      itemWidth: 15,
      itemHeight: 10,
      icon: 'roundRect',
      textStyle: {
        fontSize: 12,
        color: getThemeColor('body-color')
      },
      data: showBoth
        ? [seriesNames.currentCountName, seriesNames.currentAmountName, seriesNames.prevCountName, seriesNames.prevAmountName]
        : [seriesNames.currentName, seriesNames.prevName]
    },
    xAxis: [
      {
        type: 'category',
        data: dates,
        axisLabel: {
          formatter: (value: Date) => {
            // For month period, show days (DD MMM)
            // For 3-month period, show month only (MMM)
            return isPeriodMonth
              ? dayjs(value).format('DD MMM')
              : dayjs(value).format('DD') ;
          },
          interval: interval,
          showMinLabel: true,
          showMaxLabel: false,
          color: getThemeColor('secondary-color'),
          align: 'left',
          fontFamily: 'Nunito Sans',
          fontWeight: 600,
          fontSize: 12.8
        },
        axisLine: {
          show: true,
          lineStyle: {
            color: getThemeColor('secondary-bg')
          }
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: true,
          interval: 0,
          lineStyle: {
            color:
              theme === 'dark'
                ? getThemeColor('body-highlight-bg')
                : getThemeColor('secondary-bg')
          }
        },
        boundaryGap: false
      },
      {
        type: 'category',
        position: 'bottom',
        data: dates,
        axisLabel: {
          formatter: (value: Date) => isPeriodMonth
            ? dayjs(value).format('DD MMM')
            : dayjs(value).format('DD'),
          interval: dates.length - 1, // Show only the last date
          showMaxLabel: true,
          showMinLabel: false,
          color: getThemeColor('secondary-color'),
          align: 'right',
          fontFamily: 'Nunito Sans',
          fontWeight: 600,
          fontSize: 12.8
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        splitLine: {
          show: false
        },
        boundaryGap: false
      }
    ],
    yAxis: showBoth ? [
      {
        // First y-axis for counts
        position: 'left',
        axisPointer: { type: 'none' },
        axisTick: 'none',
        splitLine: {
          show: false
        },
        axisLine: { show: false },
        axisLabel: { show: false }
      },
      {
        // Second y-axis for amounts
        position: 'right',
        axisPointer: { type: 'none' },
        axisTick: 'none',
        splitLine: {
          show: false
        },
        axisLine: { show: false },
        axisLabel: { show: false }
      }
    ] : {
      // Single y-axis for non-showBoth mode
      position: 'right',
      axisPointer: { type: 'none' },
      axisTick: 'none',
      splitLine: {
        show: false
      },
      axisLine: { show: false },
      axisLabel: { show: false }
    },
    series: showBoth ? [
      // Current period counts
      {
        name: seriesNames.currentCountName,
        type: 'line',
        data: chartData.currentCounts,
        showSymbol: false,
        symbol: 'circle',
        yAxisIndex: 0,
        lineStyle: {
          width: 2
        }
      },
      // Current period amounts
      {
        name: seriesNames.currentAmountName,
        type: 'line',
        data: chartData.currentAmounts,
        showSymbol: false,
        symbol: 'circle',
        yAxisIndex: 1
      },
      // Previous period counts
      {
        name: seriesNames.prevCountName,
        type: 'line',
        data: chartData.prevCounts,
        lineStyle: {
          type: 'dashed',
          width: 1,
          color: getThemeColor('info')
        },
        showSymbol: false,
        symbol: 'circle',
        yAxisIndex: 0
      },
      // Previous period amounts
      {
        name: seriesNames.prevAmountName,
        type: 'line',
        data: chartData.prevAmounts,
        lineStyle: {
          type: 'dashed',
          width: 1,
          color: getThemeColor('warning')
        },
        showSymbol: false,
        symbol: 'circle',
        yAxisIndex: 1
      }
    ] : [
      {
        name: seriesNames.currentName,
        type: 'line',
        data: chartData.currentValues,
        showSymbol: false,
        symbol: 'circle'
      },
      {
        name: seriesNames.prevName,
        type: 'line',
        data: chartData.prevValues,
        lineStyle: {
          type: 'dashed',
          width: 1,
          color: getThemeColor('info')
        },
        showSymbol: false,
        symbol: 'circle'
      }
    ],
    grid: {
      right: 2,
      left: 5,
      bottom: '20px',
      top: '40px', // Increased to make room for the legend at the top
      containLabel: false
    },
    animation: false
  });

  return (
    <ReactEChartsCore
      echarts={echarts}
      option={getChartOptions()}
      style={{ height: '316px', width: '100%' }}
      notMerge={true}
    />
  );
};

export default EcomTotalSellsChart;
